import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { LockClosedIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../components/ui';
import { authService } from '../services/auth.service';

const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValid(false);
        setError('Invalid or missing reset link');
        setIsValidating(false);
        return;
      }

      try {
        const result = await authService.validateResetToken(token);
        setIsValid(result.valid);
        if (!result.valid) {
          setError('This reset link is invalid or has expired');
        }
      } catch (err: any) {
        setIsValid(false);
        setError(err.response?.data?.error || 'This reset link is invalid or has expired');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      await authService.resetPassword(token, data.password);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-secondary-900">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-secondary-600 dark:text-secondary-400">Validating your reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-secondary-900">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CH</span>
              </div>
              <span className="font-display text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Capture Health
              </span>
            </div>

            {isSuccess ? (
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-success-100 dark:bg-success-900/30">
                  <CheckCircleIcon className="w-8 h-8 text-success-600 dark:text-success-400" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 text-center">Password reset!</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400 text-center">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
              </>
            ) : !isValid ? (
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 dark:bg-danger-900/30">
                  <ExclamationTriangleIcon className="w-8 h-8 text-danger-600 dark:text-danger-400" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 text-center">Invalid link</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400 text-center">
                  {error}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Create new password</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400">
                  Your new password must be at least 8 characters long.
                </p>
              </>
            )}
          </div>

          {isSuccess ? (
            <Link to="/login">
              <Button className="w-full" size="lg">
                Sign in
              </Button>
            </Link>
          ) : !isValid ? (
            <div className="space-y-4">
              <Link to="/forgot-password">
                <Button className="w-full" size="lg">
                  Request new link
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary" className="w-full" size="lg">
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <Input
                label="New password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Enter your new password"
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />

              <Input
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm your new password"
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              {error && (
                <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
              >
                Reset password
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:block relative flex-1 gradient-mesh bg-secondary-50 dark:bg-secondary-800">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-white dark:bg-secondary-700 shadow-elevated flex items-center justify-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">CH</span>
              </div>
            </div>
            <h3 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
              Secure Password Reset
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Choose a strong password that you don't use anywhere else.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
